import { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import AuthFrame from '../components/auth/AuthFrame'
import {
  authErrorKey,
  clearAuthSession,
  formatNational,
  isThrottled,
  loadAuthSession,
  nationalNumberOk,
  saveAuthSession,
} from '../lib/authFlow'
import { E164, FALLBACK_COUNTRIES, countriesFromApi, countryName, groupByRegion, isSelectable, matchesSearch, reconcileSelection, toE164 } from '../lib/countries'
import { OTP_LENGTH, codeComplete, codeLengthWord, countdownLabel, normalizeCode, phoneHint, verifyLabel } from '../lib/otp'
import { isSiteMode, postLoginDestination } from '../lib/siteMode'
import { receiveOtp } from '../lib/webOtp'
import { fetchCountries } from '../services/auth'
import { useAuthStore } from '../store/authStore'

/** How long the code step waits for WebOTP: the code's own 2-minute life plus slack for a slow SMS. */
const WEB_OTP_WAIT_MS = 3 * 60 * 1000
/** The resend pace when the server did not say (a 429 without Retry-After). */
const DEFAULT_RESEND_SECONDS = 60

/*
 * The darker green: white on good-deep is 4.0:1 light / 4.3:1 dark (the old
 * site-good was 2.55 / 2.30, under the 3:1 a large-text button needs). Busy
 * keeps the colour (aria-busy) instead of turning into a grey "disabled".
 */
const PRIMARY =
  'site-btn inline-flex h-14 w-full items-center justify-center gap-2 rounded-[18px] bg-good-deep font-display text-[18px] font-extrabold text-white [--press:color-mix(in_srgb,var(--color-good-deep)_62%,black)] disabled:cursor-not-allowed disabled:opacity-55 aria-busy:opacity-100'
const LINK_BUTTON =
  'inline-flex min-h-11 items-center px-1 font-display text-[15px] font-bold text-brand-600 hover:underline disabled:text-ink-2 disabled:no-underline'
const SHORT_HIDE = '[@media(max-height:520px)]:hidden group-[.kb-open]/auth:hidden'

/** The ISO code in a flag-sized badge: emoji flags render as two letters on Windows anyway, and Android draws no emoji either. */
function CountryMark({ country, className = '' }) {
  return (
    <span aria-hidden="true" className={`inline-flex h-[18px] min-w-[24px] items-center justify-center rounded-[4px] bg-surface-2 px-1 font-en text-[10.5px] font-extrabold tracking-[0.2px] text-ink-2 ${className}`}>
      {country.code}
    </span>
  )
}

/** Closes on Escape while `active`. */
function useEscape(active, onClose) {
  useEffect(() => {
    if (!active) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [active, onClose])
}

/** True on a fine pointer (a mouse): autofocus helps there, while on a phone it would throw up the keyboard over the list. */
function finePointer() {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: fine)').matches
}

/**
 * The country list: a search field, then one heading per region in the
 * API's order. `live` rows are options; `coming_soon` rows stay visible but
 * greyed and disabled with «به‌زودی» — in search results too. A heading with
 * no match is hidden; no match at all says so. The search matches the
 * Persian and English names (ی/ي, ک/ك, ZWNJ, spaces folded), the ISO code
 * and the calling code with or without + or 00 (`matchesSearch`). Its state
 * is local, so it is empty every time the list opens. ↑/↓ move between the
 * selectable rows, Enter picks (the rows are buttons). Exported for the
 * render tests.
 */
export function CountryOptions({ countries, selected, onPick, initialQuery = '', autoFocusSearch = false }) {
  const { t, i18n } = useTranslation('auth')
  const language = i18n.resolvedLanguage ?? i18n.language
  const [query, setQuery] = useState(initialQuery)
  const groups = groupByRegion(countries.filter((country) => matchesSearch(country, query)))

  function onListKeyDown(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    const options = [...event.currentTarget.querySelectorAll('[role="option"]:not([disabled])')]
    if (options.length === 0) return
    event.preventDefault()
    const index = options.indexOf(document.activeElement)
    const next = event.key === 'ArrowDown' ? Math.min(options.length - 1, index + 1) : Math.max(0, index - 1)
    options[index === -1 ? 0 : next].focus()
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col" onKeyDown={onListKeyDown}>
      <div className="shrink-0 border-b border-line p-2">
        <input
          type="search"
          dir="auto"
          // Desktop only (finePointer): on a phone the keyboard would cover half the list.
          autoFocus={autoFocusSearch}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('searchCountry')}
          aria-label={t('searchCountry')}
          autoComplete="off"
          className="h-11 w-full rounded-xl border-2 border-line-2 bg-ground px-3 text-[16px] font-semibold text-ink outline-none placeholder:text-ink-2 focus:border-brand-500"
        />
      </div>
      <ul role="listbox" aria-label={t('pickCountry')} className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1.5">
        {groups.length === 0 && (
          <li role="presentation" className="px-3 py-5 text-center text-[13.5px] text-ink-2">
            {t('noCountryFound')}
          </li>
        )}
        {groups.map((group) => (
          <li key={group.region} role="presentation">
            <p className="px-3 pb-1 pt-2 text-[11.5px] font-bold text-ink-2">{t(`regions.${group.region}`, { defaultValue: group.region })}</p>
            <ul role="group" className="space-y-0.5">
              {group.countries.map((option) => {
                const current = option.code === selected?.code
                const open = isSelectable(option)
                return (
                  <li key={option.code}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={current}
                      aria-disabled={open ? undefined : true}
                      disabled={!open}
                      onClick={() => onPick(option)}
                      className={`flex h-12 w-full items-center justify-between gap-2 rounded-xl px-3 text-[14.5px] font-semibold outline-offset-[-2px] ${
                        current ? 'bg-brand-50 text-brand-700' : open ? 'text-ink hover:bg-surface-2' : 'cursor-not-allowed text-ink-3'
                      }`}
                    >
                      <span className={`flex min-w-0 items-center gap-2 ${open ? '' : 'opacity-60'}`}>
                        <CountryMark country={option} />
                        <span className="truncate">{countryName(option, language)}</span>
                      </span>
                      {open ? (
                        <span dir="ltr" className="tnum text-[13.5px] text-ink-2">
                          +{option.callingCode}
                        </span>
                      ) : (
                        <span className="shrink-0 rounded-full bg-surface-2 px-2 py-0.5 text-[11px] font-bold text-ink-2">{t('comingSoon')}</span>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Below `sm` a bottom sheet over a scrim (role=dialog, aria-modal), the
 * search on top; from `sm` up a popover under the number field. Either
 * closes on Escape, on the scrim, or by picking.
 */
function CountryPicker({ countries, selected, onPick, onClose }) {
  const { t } = useTranslation('auth')
  useEscape(true, onClose)
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40 sm:hidden" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('pickCountry')}
        className="fixed inset-x-0 bottom-0 z-50 flex max-h-[min(80svh,560px)] flex-col overflow-hidden rounded-t-[24px] border-t-2 border-line bg-surface pb-[env(safe-area-inset-bottom)] shadow-[0_-8px_30px_rgba(0,0,0,0.18)] sm:absolute sm:inset-x-0 sm:bottom-auto sm:top-[calc(100%+6px)] sm:max-h-[min(420px,60svh)] sm:rounded-2xl sm:border-2 sm:pb-0 sm:shadow-lg"
        style={{ animation: 't-rise .22s ease-out both' }}
      >
        <div className="flex shrink-0 items-center justify-between px-4 pb-1 pt-2.5 sm:hidden">
          <span className="font-display text-[17px] font-extrabold">{t('pickCountry')}</span>
          <button type="button" onClick={onClose} className="inline-flex min-h-11 items-center px-2 font-display text-[15px] font-bold text-brand-600">
            {t('close')}
          </button>
        </div>
        <CountryOptions countries={countries} selected={selected} onPick={onPick} autoFocusSearch={finePointer()} />
      </div>
    </>
  )
}

/**
 * One field, as Android draws it: `[ISO +98 ▾ | digits]`, the country
 * trigger inside the field and no country name on it — the name is in the
 * list — so the digits keep the field's width (≈180px even at 320px). The
 * whole field reads left to right in both languages.
 */
export function PhoneField({ id = 'phone', errorId = 'auth-error', disabled = false, country, countries, value, onChange, onPick, invalid, inputRef, onFocus }) {
  const { t, i18n } = useTranslation('auth')
  const language = i18n.resolvedLanguage ?? i18n.language
  const [open, setOpen] = useState(false)
  const close = () => setOpen(false)

  return (
    <div className="relative">
      <div
        dir="ltr"
        className={`flex h-14 items-center rounded-2xl border-2 bg-surface transition focus-within:border-brand-600 ${invalid ? 'border-bad-ink' : 'border-ink-3'}`}
      >
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={t('changeCountryNamed', { country: countryName(country, language) })}
          disabled={disabled}
          onClick={() => setOpen((value) => !value)}
          className="flex h-full shrink-0 items-center gap-1.5 rounded-s-2xl ps-3 pe-2.5 font-en text-[16px] font-extrabold text-ink outline-offset-[-3px]"
        >
          <CountryMark country={country} />
          <span className="tnum">+{country.callingCode}</span>
          <span aria-hidden="true" className={`text-[9px] text-ink-2 transition-transform ${open ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </button>
        <span aria-hidden="true" className="h-6 w-px shrink-0 bg-line-2" />
        <input
          id={id}
          ref={inputRef}
          disabled={disabled}
          dir="ltr"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder={phoneHint(country)}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onFocus={onFocus}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? errorId : undefined}
          className="tnum h-full min-w-0 flex-1 bg-transparent px-3 font-en text-[17px] font-bold tracking-[0.4px] text-ink outline-none placeholder:font-semibold placeholder:text-ink-2 min-[360px]:text-[18.5px]"
        />
      </div>
      {open && (
        <CountryPicker
          countries={countries}
          selected={country}
          onClose={close}
          onPick={(option) => {
            onPick(option)
            close()
            inputRef.current?.focus()
          }}
        />
      )}
    </div>
  )
}

/**
 * Six cells over one real input, as the Android `CodeField` does it: the
 * input is transparent and full-width, the cells are its decoration, so
 * the numeric keyboard, paste and `one-time-code` autofill all keep
 * working while the design keeps the cells it specifies.
 */
export function CodeField({ code, onChange, inputRef, disabled, label, invalid = false }) {
  return (
    <div className="relative" dir="ltr">
      <input
        ref={inputRef}
        value={code}
        onChange={(event) => onChange(normalizeCode(event.target.value))}
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        maxLength={OTP_LENGTH}
        disabled={disabled}
        aria-label={label}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? 'auth-error' : undefined}
        // 16px: iOS Safari zooms the page into any focused field set smaller, and this one is invisible.
        className="absolute inset-0 z-10 h-full w-full cursor-text text-[16px] opacity-0"
      />
      <div className="flex gap-2" aria-hidden="true">
        {Array.from({ length: OTP_LENGTH }, (_, index) => {
          const filled = index < code.length
          const active = index === code.length
          return (
            <span
              key={index}
              data-otp-cell
              className={`tnum flex h-14 flex-1 items-center justify-center rounded-2xl border-2 font-en text-[24px] font-extrabold ${
                invalid && filled
                  ? 'border-bad-ink bg-bad-tint text-ink'
                  : filled
                    ? 'border-brand-600 bg-brand-50 text-ink'
                    : active
                      ? 'border-brand-600 bg-surface text-ink-3'
                      : 'border-ink-3 bg-surface text-ink-3'
              }`}
            >
              {code[index] ?? ''}
            </span>
          )
        })}
      </div>
    </div>
  )
}

function Spinner() {
  return (
    <svg className="size-[18px] animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.35" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  )
}

/** «کد نمی‌رسد؟» in place: tips, then support — the code step is never left behind for a help page. */
export function CodeHelp({ onClose }) {
  const { t } = useTranslation('auth')
  const tips = t('helpTips', { returnObjects: true })
  useEscape(true, onClose)
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-help-title"
        className="fixed inset-x-0 bottom-0 z-50 mx-auto flex max-h-[85svh] w-full max-w-[480px] flex-col gap-3 overflow-y-auto rounded-t-[24px] border-t-2 border-line bg-surface px-5 pb-[max(16px,env(safe-area-inset-bottom))] pt-4 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:rounded-[24px] sm:border-2"
        style={{ animation: 't-rise .22s ease-out both' }}
      >
        <div className="flex items-center justify-between">
          <h2 id="auth-help-title" className="font-display text-[19px] font-extrabold">
            {t('helpTitle')}
          </h2>
          <button type="button" onClick={onClose} className="inline-flex min-h-11 items-center px-2 font-display text-[15px] font-bold text-brand-600">
            {t('close')}
          </button>
        </div>
        <ul className="flex flex-col gap-2.5 text-[14.5px] leading-[1.8] text-ink-2">
          {Array.isArray(tips) &&
            tips.map((tip) => (
              <li key={tip} className="flex gap-2.5">
                <span aria-hidden="true" className="mt-[11px] size-1.5 shrink-0 rounded-full bg-good-deep" />
                {tip}
              </li>
            ))}
        </ul>
        <Link
          to="/contact"
          target={isSiteMode() ? '_blank' : undefined}
          rel={isSiteMode() ? 'noopener' : undefined}
          className="inline-flex min-h-11 items-center justify-center rounded-2xl border-2 border-line font-display text-[15px] font-bold text-brand-600 hover:bg-surface-2"
        >
          {t('helpSupport')}
        </Link>
      </div>
    </>
  )
}

/**
 * Two-step phone sign-in (owner's brief 2026-10-14: fixed, no scroll, like
 * the Android login, in fa and en). There is no separate sign-up: a number
 * never seen before gets its account when its code is verified, and the
 * API does not reveal which is happening.
 *
 * - The code step is a history entry (`?step=code`): Back returns to the
 *   number, as Android's BackHandler does.
 * - The country, the number, the step and the resend deadline survive a
 *   refresh in sessionStorage (lib/authFlow.js), so a learner who reloads,
 *   or opens «کد نمی‌رسد؟», never has to ask for — and burn — another code.
 * - A 429 on the request means a code is already on its way: the code
 *   step opens, as on Android. Every error is shown in the page's language
 *   (authErrorKey), never the server's English message.
 * - Every string comes from the `auth` namespace: the page is fully Persian
 *   or fully English, never both (owner's rule, 2026-09-19).
 */
export default function Auth() {
  const { t, i18n } = useTranslation('auth')
  const { t: site } = useTranslation('site')
  const language = i18n.resolvedLanguage ?? i18n.language
  const navigate = useNavigate()
  const location = useLocation()
  const [params] = useSearchParams()
  const destination = postLoginDestination(location.state?.from)

  const requestOtp = useAuthStore((s) => s.requestOtp)
  const verifyOtp = useAuthStore((s) => s.verifyOtp)

  // Read once: the state the last visit left in this tab.
  const [saved] = useState(() => loadAuthSession())
  const [countries, setCountries] = useState(FALLBACK_COUNTRIES)
  const [country, setCountry] = useState(() => FALLBACK_COUNTRIES.find((c) => c.code === saved?.countryCode) ?? FALLBACK_COUNTRIES[0])
  const [localNumber, setLocalNumber] = useState(saved?.localNumber ?? '')
  // The number the code was sent to: the code step needs one to stand on.
  const [sentTo, setSentTo] = useState(() => (saved?.step === 'code' ? { countryCode: saved.countryCode, localNumber: saved.localNumber } : null))
  const [resendUntil, setResendUntil] = useState(() => Number(saved?.resendUntil) || 0)
  const [now, setNow] = useState(() => Date.now())
  const [code, setCode] = useState('')
  const [errorKey, setErrorKey] = useState(null)
  const [busy, setBusy] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  // Bumped on every code sent, so a resend restarts the WebOTP wait.
  const [sends, setSends] = useState(0)

  const step = params.get('step') === 'code' && sentTo ? 'code' : 'phone'
  const phoneRef = useRef(null)
  const codeRef = useRef(null)
  const primaryRef = useRef(null)
  const phone = toE164(country.callingCode, localNumber)
  const cooldown = Math.max(0, Math.ceil((resendUntil - now) / 1000))

  // A `?step=code` URL with nothing to stand on (a shared link, an old tab) is the number step.
  useEffect(() => {
    if (params.get('step') === 'code' && !sentTo) navigate('/auth', { replace: true, state: location.state })
  }, [params, sentTo, navigate, location.state])

  useEffect(() => {
    saveAuthSession({ countryCode: country.code, localNumber, step, resendUntil })
  }, [country.code, localNumber, step, resendUntil])

  // Resend countdown from the deadline, so it resumes right after a refresh.
  useEffect(() => {
    if (resendUntil <= Date.now()) return undefined
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [resendUntil])

  // A step change (including the browser's Back) starts clean: a wrong-code
  // message must not follow the learner back to the number.
  useEffect(() => {
    setErrorKey(null)
    if (step === 'code') codeRef.current?.focus()
    else if (finePointer()) phoneRef.current?.focus()
  }, [step])

  // The real list replaces the fallback once it arrives; a failed or
  // unusable answer keeps the fallback, so sign-in never depends on it.
  useEffect(() => {
    let cancelled = false
    fetchCountries()
      .then((items) => {
        const list = countriesFromApi(items)
        if (cancelled || !list) return
        setCountries(list)
        setCountry((current) => reconcileSelection(list.find((c) => c.code === saved?.countryCode) ?? current, list))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [saved])

  // WebOTP (lib/webOtp.js): on Chrome for Android the code arrives from the
  // SMS by itself and is submitted at once. Aborted when the step is left,
  // the page closes, a new code is sent, or the code would have expired;
  // anywhere WebOTP does not exist this does nothing.
  const submitRef = useRef(null)
  useEffect(() => {
    if (step !== 'code') return undefined
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), WEB_OTP_WAIT_MS)
    receiveOtp(controller.signal).then((received) => {
      if (!received || controller.signal.aborted) return
      setCode(received)
      submitRef.current?.(received)
    })
    return () => {
      clearTimeout(timeout)
      controller.abort()
    }
  }, [step, sends])

  /** With the keyboard up on a phone, keep the action button in view under the field. */
  function revealPrimary() {
    if (finePointer()) return
    setTimeout(() => primaryRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' }), 320)
  }

  function openCodeStep(deadline) {
    setSentTo({ countryCode: country.code, localNumber })
    setResendUntil(deadline)
    setNow(Date.now())
    setCode('')
    setSends((value) => value + 1)
    if (params.get('step') !== 'code') navigate('/auth?step=code', { state: location.state })
  }

  async function sendCode(event) {
    event?.preventDefault()
    if (!E164.test(phone) || !nationalNumberOk(country, localNumber)) {
      setErrorKey('invalidPhone')
      phoneRef.current?.focus()
      return
    }
    setBusy(true)
    setErrorKey(null)
    try {
      const result = await requestOtp(phone)
      const seconds = Number(result?.expiresInSeconds) > 0 ? Number(result.expiresInSeconds) : DEFAULT_RESEND_SECONDS
      openCodeStep(Date.now() + seconds * 1000)
    } catch (err) {
      if (isThrottled(err)) {
        // A code went out moments ago and is still good: wait for it, don't ask again.
        const retryAfter = Number(err?.response?.headers?.['retry-after'])
        openCodeStep(Date.now() + (Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : DEFAULT_RESEND_SECONDS) * 1000)
      } else {
        setErrorKey(authErrorKey(err, 'sendFailed'))
      }
    } finally {
      setBusy(false)
    }
  }

  async function confirmCode(event) {
    event.preventDefault()
    await submitCode(code)
  }

  async function submitCode(value) {
    if (!codeComplete(value)) return
    setBusy(true)
    setErrorKey(null)
    try {
      await verifyOtp(phone, value)
      clearAuthSession()
      navigate(destination, { replace: true })
    } catch (err) {
      // The code stays, so the learner can see what was wrong; focus comes back to fix it.
      setErrorKey(authErrorKey(err, 'wrongCode'))
      codeRef.current?.focus()
    } finally {
      setBusy(false)
    }
  }

  submitRef.current = submitCode

  function clearCode() {
    setCode('')
    setErrorKey(null)
    codeRef.current?.focus()
  }

  function editNumber() {
    setCode('')
    setErrorKey(null)
    navigate('/auth', { replace: true, state: location.state })
  }

  const error = errorKey ? t(errorKey) : null
  const shownCountry = countries.find((c) => c.code === sentTo?.countryCode) ?? country
  const shownNumber = `+${shownCountry.callingCode} ${formatNational(shownCountry, sentTo?.localNumber ?? localNumber)}`
  const privacy = (
    <Link to="/privacy" target={isSiteMode() ? '_blank' : undefined} rel={isSiteMode() ? 'noopener' : undefined} className="font-bold text-brand-600 underline-offset-2 hover:underline" />
  )

  return (
    <AuthFrame
      title={site('pageTitle.login')}
      footer={
        <button type="button" onClick={() => setHelpOpen(true)} className={`${LINK_BUTTON} text-[14px]`}>
          {t('helpTitle')}
        </button>
      }
    >
      {step === 'phone' ? (
        <form onSubmit={sendCode} noValidate className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h1 className="font-display text-[26px] font-extrabold leading-[1.25] tracking-[-0.4px]">{t('phoneTitle')}</h1>
            <p className={`text-[14.5px] leading-[1.6] text-ink-2 ${SHORT_HIDE}`}>{t('lead', { length: codeLengthWord() })}</p>
          </div>

          <div className="flex flex-col gap-1.5 pt-1">
            <label htmlFor="phone" className="text-[13.5px] font-bold text-ink-2">
              {t('phoneLabel')}
            </label>
            <PhoneField
              country={country}
              countries={countries}
              value={localNumber}
              onChange={(value) => {
                setLocalNumber(value)
                if (errorKey) setErrorKey(null)
              }}
              onPick={(option) => {
                setCountry(option)
                setErrorKey(null)
              }}
              invalid={Boolean(error)}
              inputRef={phoneRef}
              onFocus={revealPrimary}
            />
            {error && (
              <p id="auth-error" role="alert" className="text-[13.5px] leading-[1.6] text-bad-ink">
                {error}
              </p>
            )}
          </div>

          <button ref={primaryRef} type="submit" disabled={busy} aria-busy={busy || undefined} className={`${PRIMARY} mt-1`}>
            {busy && <Spinner />}
            {busy ? t('sending') : t('sendCode')}
          </button>

          <p className="text-balance text-center text-[12.5px] leading-[1.7] text-ink-2">
            <Trans t={t} i18nKey="termsLine" components={{ privacy }} />
          </p>
        </form>
      ) : (
        <form onSubmit={confirmCode} noValidate className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h1 className="font-display text-[26px] font-extrabold leading-[1.25] tracking-[-0.4px]">{t('codeTitle')}</h1>
            <p className="text-[14.5px] leading-[1.75] text-ink-2">
              <Trans
                t={t}
                i18nKey="codeSentLine"
                values={{ length: codeLengthWord() }}
                components={{
                  phone: (
                    <bdi dir="ltr" className="tnum whitespace-nowrap font-en font-bold text-ink">
                      {shownNumber}
                    </bdi>
                  ),
                }}
              />{' '}
              <button type="button" onClick={editNumber} disabled={busy} className="-my-2 inline-flex min-h-11 items-center px-1 align-middle font-display font-bold text-brand-600 hover:underline">
                {t('editNumber')}
              </button>
            </p>
          </div>

          <div className="pt-1">
            <CodeField code={code} onChange={setCode} inputRef={codeRef} disabled={busy} label={t('codeAria')} invalid={Boolean(error)} />
          </div>

          <div className="-my-1 flex items-center justify-between gap-2.5">
            {cooldown > 0 ? (
              // A countdown is a count, not a time code — Persian digits in fa (design-system.md).
              <span className="inline-flex min-h-11 items-center text-[13.5px] text-ink-2">{t('resendIn', { countdown: countdownLabel(cooldown, language) })}</span>
            ) : (
              <button type="button" disabled={busy} onClick={() => sendCode()} className={LINK_BUTTON}>
                {t('resend')}
              </button>
            )}
            <button type="button" onClick={clearCode} className={LINK_BUTTON}>
              {t('clear')}
            </button>
          </div>

          {error && (
            <p id="auth-error" role="alert" className="text-[13.5px] leading-[1.6] text-bad-ink">
              {error}
            </p>
          )}

          <button ref={primaryRef} type="submit" disabled={!codeComplete(code) || busy} aria-busy={busy || undefined} className={PRIMARY}>
            {busy && <Spinner />}
            {verifyLabel(code, busy)}
          </button>
        </form>
      )}

      {helpOpen && <CodeHelp onClose={() => setHelpOpen(false)} />}
    </AuthFrame>
  )
}
